<template>
  <TransitionGroup
    name="height-auto"
    type="transition"
  >
    <div
      v-for="toast of toasts"
      :key="toast.id"
      class="toast"
      :class="`toast--${toast.type}`"
      :data-id="toast.id"
    >
      <span class="toast__message" v-html="toast.message"></span>
      <div class="toast__buttons">
        <button
          v-for="button of toast.buttons"
          :key="`${toast.id}__${button}`"
          type="button"
          @click.passive="removeToast(toast.id, button)"
        >
          {{ button }}
        </button>
      </div>
    </div>
  </TransitionGroup>
</template>

<script setup lang="ts">
  import { ref } from 'vue';

  /** The `resolve` callback in a Promise constructor. */
  export type PromiseResolveCb = (value: unknown | PromiseLike<unknown>) => void;

  export type Toast = {
    message: string;
    buttons: string[];
    type: 'success' | 'info' | 'warn' | 'error';
    onClose: PromiseResolveCb;
    toastTimeout: number;
    id: number;
  };

  export type ToastConfig = {
    timeout?: Toast['toastTimeout'];
    buttons?: Toast['buttons'];
    type?: Toast['type'];
  };

  const toasts = ref<Toast[]>([]);

  const removeToast = (id: Toast['id'], button?: Toast['buttons'][number] | null) => {
    const toast = toasts.value.find((t) => t.id === id);

    if (toast) {
      window.clearTimeout(toast.toastTimeout);
      toasts.value = toasts.value.filter((item) => item !== toast);
      toast.onClose(button);
    }
  };

  const addToast = (
    message: Toast['message'],
    id: Toast['id'],
    onClose: Toast['onClose'],
    {
      timeout,
      buttons,
      type,
    }: NonNullableObject<ToastConfig>,
  ) => {
    const toast: Toast = {
      message,
      buttons,
      type,
      onClose,
      toastTimeout: 0,
      id,
    };

    if (timeout) {
      toast.toastTimeout = window.setTimeout(() => {
        removeToast(id, null);
      }, timeout);
    }

    toasts.value.push(toast);
  };

  defineExpose({
    removeToast,
    addToast,
  });
</script>
